// Pure cycle helper functions — no React dependency.
// Shared by CycleSelector.jsx and unit tests.

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const DAY_BITS = { Monday: 1, Tuesday: 2, Wednesday: 4, Thursday: 8, Friday: 16, Saturday: 32, Sunday: 64 };

// Maps JS getDay() (0=Sun) to ordering_days bitmask bit
export const JS_DAY_TO_BIT = [64, 1, 2, 4, 8, 16, 32];

export const ALL_DAYS_BITMASK = 127;

export function toLocalDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Derive the set of selected DAY_NAMES from a cycle (for weekly-compatible presets)
export function cycleToWeeklyDays(cycle) {
  if (!cycle) return [];
  const preset = cycle.preset || '';
  if (preset === 'every_day') return [...DAY_NAMES];
  if (preset.startsWith('every_')) {
    const name = preset.slice(6);
    const cap = name.charAt(0).toUpperCase() + name.slice(1);
    return DAY_NAMES.includes(cap) ? [cap] : [];
  }
  if (preset === 'weekly_choice' || preset === 'twice_weekly') return cycle.days || [];
  if (preset === 'custom' && (cycle.unit || 'weeks') === 'weeks') return cycle.weekDays || [];
  return [];
}

// Sorted JS getDay() indices (0=Sun … 6=Sat) a weekly-compatible cycle lands
// on. Reads the saved `{preset:'every_monday', days:[…]}` family via
// cycleToWeeklyDays, plus the numeric `{daysOfWeek:[1]}` shape some rows carry.
// Monthly / fiscal / interval presets have no fixed weekday and return [].
export function cycleToDowIndices(cycle) {
  if (!cycle) return [];
  let indices;
  if (Array.isArray(cycle.daysOfWeek)) {
    indices = cycle.daysOfWeek.filter(n => Number.isInteger(n) && n >= 0 && n <= 6);
  } else {
    indices = cycleToWeeklyDays(cycle)
      .map(name => JS_DAY_TO_BIT.indexOf(DAY_BITS[name]))
      .filter(i => i >= 0);
  }
  return [...new Set(indices)].sort((a, b) => a - b);
}

// The weekday (JS getDay() index) a scheduled template belongs under when
// grouping the Ordering section by day: the next scheduled day on/after
// fromDow, wrapping into next week. Null when the cycle has no weekly weekday
// — callers fall back to the session date, as before.
export function nextScheduleDow(cycle, fromDow) {
  const indices = cycleToDowIndices(cycle);
  if (indices.length === 0) return null;
  const start = Number.isInteger(fromDow) ? fromDow : new Date().getDay();
  for (const idx of indices) if (idx >= start) return idx;
  return indices[0];
}

// Convert a cycle object to a 7-bit ordering_days bitmask.
// Weekly presets map exactly. Monthly/daily-interval presets → 0.
// Full cycle fidelity is kept in ordering_cycle_config JSONB column.
export function cycleToBitmask(cycle) {
  if (!cycle) return 0;
  if (cycle.preset === 'every_day') return ALL_DAYS_BITMASK;
  const days = cycleToWeeklyDays(cycle);
  return days.reduce((acc, d) => acc | (DAY_BITS[d] || 0), 0);
}

// Convert a bitmask back to the most natural cycle shape.
export function bitmaskToCycle(bitmask) {
  const n = Number(bitmask || 0);
  if (!n) return null;
  if (n === ALL_DAYS_BITMASK) return { preset: 'every_day', days: [...DAY_NAMES] };
  const days = DAY_NAMES.filter(d => (n & DAY_BITS[d]) !== 0);
  if (days.length === 0) return null;
  if (days.length === 1) return { preset: `every_${days[0].toLowerCase()}`, days };
  if (days.length === 2) return { preset: 'twice_weekly', days };
  return { preset: 'weekly_choice', days };
}

// Return the next date string (YYYY-MM-DD, local) for a fixed day-of-month,
// searching strictly after `from`. When the target day exceeds the month's
// length, it is capped to the last day of that month (e.g., day 31 in April
// → April 30; day 31 following Jan 31 → Feb 28).
export function nextMonthDay(from, dayNum) {
  let year = from.getFullYear();
  let month = from.getMonth();
  for (let i = 0; i < 24; i++) {
    const lastDay = new Date(year, month + 1, 0).getDate();
    const day = Math.min(dayNum, lastDay);
    const candidate = new Date(year, month, day);
    if (candidate > from) return toLocalDateStr(candidate);
    month++;
    if (month > 11) { month = 0; year++; }
  }
  const fallback = new Date(from);
  fallback.setDate(fallback.getDate() + 1);
  return toLocalDateStr(fallback);
}

// Find the ordinal weekday (e.g. "first Tuesday") in a specific calendar month.
// `monthStart` must be a Date at day 1 of the target month.
// Returns a Date object, or undefined if not found.
export function ordinalWeekdayInMonth(monthStart, pos, weekdayName) {
  const posMap = { first: 1, second: 2, third: 3, last: -1 };
  const targetBit = DAY_BITS[weekdayName] || 1;
  const posNum = posMap[pos] || 1;
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const occurrences = [];
  for (let day = 1; day <= lastDay; day++) {
    const d = new Date(year, month, day);
    if (JS_DAY_TO_BIT[d.getDay()] === targetBit) occurrences.push(d);
  }
  return posNum === -1 ? occurrences[occurrences.length - 1] : occurrences[posNum - 1];
}

// Return the next ordinal weekday occurrence after `from` (searching forward month by month).
export function nextOrdinalDay(from, pos, weekdayName) {
  for (let mo = 0; mo <= 12; mo++) {
    const monthStart = new Date(from.getFullYear(), from.getMonth() + mo, 1);
    const target = ordinalWeekdayInMonth(monthStart, pos, weekdayName);
    if (target && target > from) return toLocalDateStr(target);
  }
  const fallback = new Date(from);
  fallback.setDate(fallback.getDate() + 7);
  return toLocalDateStr(fallback);
}

// Compact human-readable label for a cycle (for template cards / lists).
// Examples:
//   { preset:'eop_454' }                         -> 'Monthly (4-5-4)'
//   { preset:'eop_544' }                         -> 'Monthly (5-4-4)'
//   { preset:'every_day' }                       -> 'Every day'
//   { preset:'every_monday' }                    -> 'Every Mon'
//   { preset:'weekly_choice', days:['Monday','Friday'] } -> 'Mon & Fri'
//   { preset:'twice_weekly',  days:['Tuesday','Thursday'] } -> 'Tue & Thu'
//   { preset:'monthly_1' }                       -> '1st of month'
//   { preset:'monthly_15' }                      -> '15th of month'
//   { preset:'monthly_last' }                    -> 'Last day of month'
//   { preset:'custom', interval:2, unit:'weeks', weekDays:['Monday'] }
//                                                -> 'Every 2 weeks on Mon'
export function cycleToFriendlyLabel(cycle) {
  if (!cycle || !cycle.preset) return '';
  if (cycle.preset === 'none') return cycle.skipped === true ? 'No schedule' : '';
  const shortOf = (name) => (name || '').slice(0, 3);
  const joinShort = (days) => {
    const arr = (days || []).map(shortOf).filter(Boolean);
    if (arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]} & ${arr[1]}`;
    return `${arr.slice(0, -1).join(', ')} & ${arr[arr.length - 1]}`;
  };
  const preset = cycle.preset;
  if (preset === 'eop_454') return 'Monthly (4-5-4)';
  if (preset === 'eop_544') return 'Monthly (5-4-4)';
  if (preset === 'eop_445') return 'Monthly (4-4-5)';
  if (preset === 'every_day') return 'Every day';
  if (preset === 'monthly_1') return '1st of month';
  if (preset === 'monthly_15') return '15th of month';
  if (preset === 'monthly_last') return 'Last day of month';
  if (preset.startsWith('every_')) {
    const name = preset.slice(6);
    const cap = name.charAt(0).toUpperCase() + name.slice(1);
    return `Every ${shortOf(cap)}`;
  }
  if (preset === 'weekly_choice' || preset === 'twice_weekly') {
    const joined = joinShort(cycle.days);
    if (joined) return joined;
    return preset === 'twice_weekly' ? 'Twice a week' : 'Weekly';
  }
  if (preset === 'custom') {
    const interval = Math.max(1, parseInt(cycle.interval, 10) || 1);
    const unit = cycle.unit || 'weeks';
    const unitSingular = unit.replace(/s$/, '');
    const base = interval === 1 ? `Every ${unitSingular}` : `Every ${interval} ${unit}`;
    if (unit === 'weeks') {
      const joined = joinShort(cycle.weekDays);
      return joined ? `${base} on ${joined}` : base;
    }
    if (unit === 'months') {
      if ((cycle.monthType || 'day') === 'day') {
        const d = Math.max(1, Math.min(31, cycle.monthDay || 1));
        const suf = d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th';
        return `${base} on ${d}${suf}`;
      }
      const pos = cycle.ordinalPos || 'first';
      const wd = shortOf(cycle.ordinalWeekday || 'Monday');
      return `${base} on ${pos} ${wd}`;
    }
    return base;
  }
  return preset;
}

// Decide how a cycle should be displayed on template metadata surfaces.
// Most cycles land on a predictable weekday or day-of-month and read
// well as a short label ("Every Saturday", "1st of month", "Mon & Fri").
// NRF fiscal 4-5-4 / 5-4-4 schedules land on different weekdays AND
// different days-of-month each period, so a single label hides the real
// cadence — in that case we surface the next three period-end dates.
// Returns null when the cycle has no preset.
// Shape:
//   { kind: 'label', text }                - render as a single-line label
//   { kind: 'dates', dates: [...], label } - render each YYYY-MM-DD on its own row
export function cycleToScheduleDisplay(cycle, fromDate) {
  if (!cycle || !cycle.preset) return null;
  if (cycle.preset === 'none') {
    return cycle.skipped === true ? { kind: 'label', text: 'No schedule', muted: true } : null;
  }
  const preset = cycle.preset;
  const label = cycleToFriendlyLabel(cycle);
  if (preset === 'eop_454' || preset === 'eop_544' || preset === 'eop_445') {
    const list = Array.isArray(cycle.periodEndDates) ? cycle.periodEndDates : [];
    const base = fromDate || toLocalDateStr(new Date());
    const future = list.filter(d => d > base).slice(0, 3);
    if (future.length >= 2) return { kind: 'dates', dates: future, label };
    return { kind: 'label', text: label };
  }
  return { kind: 'label', text: label };
}

// Convert a store's inventory_period_config (shape produced by store-setup.html)
// into a canonical cycle object suitable for count_sessions.ordering_cycle_config.
// Accepted schedule_type values:
//   '4-5-4'     -> { preset:'eop_454', periodEndDates:[...] }
//   '5-4-4'     -> { preset:'eop_544', periodEndDates:[...] }
//   'month_end' -> { preset:'monthly_last' }
//   'custom_day'-> { preset:'monthly_<d>' } for d in {1,15}, else a custom
//                  monthly cycle on that day-of-month.
export function inventoryPeriodConfigToCycle(cfg) {
  if (!cfg || !cfg.enabled || !cfg.schedule_type) return null;
  const type = String(cfg.schedule_type);
  const periodEndDates = Array.isArray(cfg.period_end_dates) ? cfg.period_end_dates : [];
  if (type === '4-5-4' || type === '454') return { preset: 'eop_454', periodEndDates };
  if (type === '5-4-4' || type === '544') return { preset: 'eop_544', periodEndDates };
  if (type === '4-4-5' || type === '445') return { preset: 'eop_445', periodEndDates };
  if (type === 'month_end') return { preset: 'monthly_last' };
  // Rolling cadences the store wizard offers but had no cycle mapping before,
  // so the EOP template used to land with NO schedule even though one was set.
  if (type === 'every_4_weeks') return { preset: 'custom', interval: 4, unit: 'weeks' };
  if (type === 'weekly') return { preset: 'custom', interval: 1, unit: 'weeks' };
  if (type === 'custom_day') {
    const day = Math.max(1, Math.min(28, parseInt(cfg.custom_day, 10) || 15));
    if (day === 1) return { preset: 'monthly_1' };
    if (day === 15) return { preset: 'monthly_15' };
    return { preset: 'custom', interval: 1, unit: 'months', monthType: 'day', monthDay: day };
  }
  // 'custom' free-text ("Something else") has no deterministic recurrence.
  return null;
}

// Return the next date (YYYY-MM-DD, local) on which this cycle fires,
// starting strictly AFTER fromDate.
//
// Semantics for custom cycles:
//   - "Every N days": adds exactly N days (the interval IS the gap).
//   - "Every N weeks on [days]": the earliest matching weekday that falls at
//     least (N-1)*7 days after `base` (i.e., in the Nth weekly window).
//     interval=1 → nearest matching weekday from tomorrow;
//     interval=2 → nearest matching weekday from (base+7).
//   - "Every N months": advance the search base by (N-1) months, then find
//     the nearest future occurrence of the day/ordinal from that shifted base.
//     interval=1 → works like the fixed monthly presets;
//     interval=2 → starts searching one month out.
export function getNextOccurrence(cycle, fromDate) {
  const base = fromDate ? new Date(fromDate + 'T00:00:00') : new Date();
  const start = new Date(base);
  start.setDate(start.getDate() + 1); // tomorrow

  if (!cycle || !cycle.preset) return toLocalDateStr(start);
  if (cycle.preset === 'none') return null;

  const preset = cycle.preset;

  // NRF fiscal period-end presets — pick the next period_end_date strictly after `base`.
  // If the configured list is empty or fully in the past, fall back to "tomorrow"
  // rather than returning a stale past date.
  if (preset === 'eop_454' || preset === 'eop_544' || preset === 'eop_445') {
    const list = Array.isArray(cycle.periodEndDates) ? cycle.periodEndDates : [];
    const baseStr = toLocalDateStr(base);
    const next = list.find(d => d > baseStr);
    if (next) return next;
    return toLocalDateStr(start);
  }

  // Every day — just tomorrow
  if (preset === 'every_day') return toLocalDateStr(start);

  // Single-weekday presets: nearest matching weekday from tomorrow
  if (preset.startsWith('every_')) {
    const name = preset.slice(6);
    const cap = name.charAt(0).toUpperCase() + name.slice(1);
    const targetBit = DAY_BITS[cap];
    if (targetBit) {
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        if (JS_DAY_TO_BIT[d.getDay()] === targetBit) return toLocalDateStr(d);
      }
    }
  }

  // Weekly choice / twice weekly: nearest matching weekday from tomorrow
  if (preset === 'weekly_choice' || preset === 'twice_weekly') {
    const bits = (cycle.days || []).map(d => DAY_BITS[d]).filter(Boolean);
    if (bits.length > 0) {
      for (let i = 0; i < 7; i++) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        if (bits.includes(JS_DAY_TO_BIT[d.getDay()])) return toLocalDateStr(d);
      }
    }
  }

  // Fixed monthly presets: next occurrence of that day-of-month
  if (preset === 'monthly_1') return nextMonthDay(base, 1);
  if (preset === 'monthly_15') return nextMonthDay(base, 15);
  // Last day of the month: day=31 caps to actual month length (Feb→28/29 etc.)
  if (preset === 'monthly_last') return nextMonthDay(base, 31);

  if (preset === 'custom') {
    const interval = Math.max(1, parseInt(cycle.interval, 10) || 1);
    const unit = cycle.unit || 'weeks';

    // Every N days: add exactly N days from base
    if (unit === 'days') {
      const d = new Date(base);
      d.setDate(d.getDate() + interval);
      return toLocalDateStr(d);
    }

    // Every N weeks: search from max(tomorrow, base + (N-1)*7 days).
    // This means interval=1 finds the nearest weekday; interval=2 skips one
    // week and finds the nearest weekday from there, etc.
    if (unit === 'weeks') {
      const bits = (cycle.weekDays || []).map(d => DAY_BITS[d]).filter(Boolean);
      const minDate = new Date(base);
      minDate.setDate(minDate.getDate() + (interval - 1) * 7);
      const searchFrom = minDate > start ? minDate : start;
      if (bits.length > 0) {
        for (let i = 0; i < 7; i++) {
          const d = new Date(searchFrom);
          d.setDate(d.getDate() + i);
          if (bits.includes(JS_DAY_TO_BIT[d.getDay()])) return toLocalDateStr(d);
        }
      }
      return toLocalDateStr(searchFrom);
    }

    // Every N months: advance search base by (N-1) months, then find the
    // nearest occurrence of the day/ordinal pattern strictly after that base.
    if (unit === 'months') {
      const minBase = new Date(base.getFullYear(), base.getMonth() + (interval - 1), base.getDate());
      if ((cycle.monthType || 'day') === 'day') {
        return nextMonthDay(minBase, Math.max(1, Math.min(31, cycle.monthDay || 1)));
      } else {
        return nextOrdinalDay(minBase, cycle.ordinalPos || 'first', cycle.ordinalWeekday || 'Monday');
      }
    }
  }

  return toLocalDateStr(start);
}
